/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts"
import { Download, Filter, Search, Trophy, Medal, Award, Shield, TrendingUp, Users } from "lucide-react"
import { Link } from "react-router-dom"
import bgImage from "@/assets/favicon.png"
import apiService from "@/services/apiService"
import { getUserInfo } from "@/services/userService"

interface OfficerGDData {
  id: string
  officerName: string
  rank: string
  badgeNumber: string
  policeStation: string
  department: string
  gdEntriesCount: number
  thisMonth: number
  lastMonth: number
  avgProcessingTime: string
  completionRate: number
  caseTypes: { [key: string]: number }
  trend: "UP" | "DOWN" | "STABLE"
  trendPercentage: number
}

// Dummy data
const officersSampleData: OfficerGDData[] = [
  {
    id: "1",
    officerName: "Inspector Sharma",
    rank: "Inspector",
    badgeNumber: "INS001",
    policeStation: "Bandra",
    department: "Crime Branch",
    gdEntriesCount: 45,
    thisMonth: 15,
    lastMonth: 12,
    avgProcessingTime: "2.5 hours",
    completionRate: 95,
    caseTypes: { प्रौढ़: 18, विवाद: 12, Theft: 8, Assault: 5, Fraud: 2 },
    trend: "UP",
    trendPercentage: 25,
  },
]

const COLORS = ["#000000", "#404040", "#666666", "#808080", "#999999", "#BBBBBB"]

export default function OfficerWiseReport() {
  const [officersData, setOfficersData] = useState<OfficerGDData[]>([])
  const [filteredData, setFilteredData] = useState<OfficerGDData[]>(officersSampleData)
  const [filters, setFilters] = useState({
    policeStation: "",
    department: "",
    rank: "",
    dateRange: "thisMonth",
  })
  const [searchTerm, setSearchTerm] = useState("")

  const policeStations = ["All", "Bandra", "Andheri", "Borivali", "Malad"]
  const departments = ["All", "Crime Branch", "Traffic", "Investigation", "Patrol", "Security"]
  const ranks = ["All", "Inspector", "Sub-Inspector", "Assistant Sub-Inspector", "Head Constable", "Constable"]

  useEffect(() => {
    getOfficerStats();
  },[])

  const getOfficerStats = async () => {
    const queryParams: any = {}
    const userInfo:any = getUserInfo();
    const userId = userInfo?(userInfo.id?userInfo.id:null):null;
    if (userId) queryParams["user_id"] = userId;
    const jsonOfficerStats:any = await apiService.get('general-diaries/officer-stats', queryParams);
    setOfficersData(jsonOfficerStats);
    setFilteredData(jsonOfficerStats);
  }

  const applyFilters = () => {
    let filtered = officersData

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.officerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.badgeNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.policeStation.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    if (filters.policeStation && filters.policeStation !== "All") {
      filtered = filtered.filter((item) => item.policeStation === filters.policeStation)
    }

    if (filters.department && filters.department !== "All") {
      filtered = filtered.filter((item) => item.department === filters.department)
    }

    if (filters.rank && filters.rank !== "All") {
      filtered = filtered.filter((item) => item.rank === filters.rank)
    }

    setFilteredData(filtered)
  }

  const getRankBadge = (rank: string) => {
    const rankConfig = {
      Inspector: { color: "bg-purple-100 text-purple-800", icon: Shield },
      "Sub-Inspector": { color: "bg-blue-100 text-blue-800", icon: Medal },
      "Assistant Sub-Inspector": { color: "bg-green-100 text-green-800", icon: Award },
      "Head Constable": { color: "bg-orange-100 text-orange-800", icon: Trophy },
      Constable: { color: "bg-gray-100 text-gray-800", icon: Users },
    }
    const config = rankConfig[rank as keyof typeof rankConfig] || rankConfig.Constable
    return <Badge className={`${config.color} hover:${config.color}`}>{rank}</Badge>
  }

  // const getTrendBadge = (trend: string, percentage: number) => {
  //   if (trend === "UP") {
  //     return (
  //       <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
  //         <TrendingUp className="h-3 w-3 mr-1" />+{percentage}%
  //       </Badge>
  //     )
  //   } else if (trend === "DOWN") {
  //     return (
  //       <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
  //         <TrendingUp className="h-3 w-3 mr-1 rotate-180" />-{percentage}%
  //       </Badge>
  //     )
  //   } else {
  //     return (
  //       <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
  //         <div className="h-3 w-3 mr-1 bg-gray-400 rounded-full"></div>
  //         {percentage}%
  //       </Badge>
  //     )
  //   }
  // }

  const exportToCSV = () => {
    const headers = [
      "Officer Name",
      "Rank",
      "Badge Number",
      "Police Station",
      "Department",
      "Total GD Entries",
      "This Month",
      "Last Month",
      "Avg Processing Time",
      "Completion Rate",
      "Trend",
    ]
    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) =>
        [
          row.officerName,
          row.rank,
          row.badgeNumber,
          row.policeStation,
          row.department,
          row.gdEntriesCount,
          row.thisMonth,
          row.lastMonth,
          row.avgProcessingTime,
          row.completionRate,
          `${row.trend} ${row.trendPercentage}%`,
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `officer-wise-gd-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
  }

  const totalGDEntries = filteredData.reduce((sum, officer) => sum + officer.gdEntriesCount, 0)
  const avgCompletionRate = Math.round(
    filteredData.reduce((sum, officer) => sum + officer.completionRate, 0) / filteredData.length,
  )
  const topPerformer = filteredData.length ? filteredData.reduce((top, officer) => officer.gdEntriesCount > top.gdEntriesCount ? officer : top) : null;


  // Prepare chart data
  const chartData = filteredData
    .sort((a, b) => b.gdEntriesCount - a.gdEntriesCount)
    .slice(0, 10)
    .map((officer) => ({
      name: officer.officerName,
      gdEntries: officer.gdEntriesCount,
      thisMonth: officer.thisMonth,
      completionRate: officer.completionRate,
    }))

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Link to="/reports" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
              ← Back to Reports
            </Link>
            <h1 className="text-3xl font-bold text-black">Officer-wise GD Entry Report</h1>
            <p className="text-gray-600 mt-1">Track GD entries by officers with leaderboard format</p>
          </div>
          <Button onClick={exportToCSV} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="text-2xl font-bold text-black">{filteredData.length}</div>
                  <p className="text-sm text-gray-600">Active Officers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Trophy className="h-5 w-5 text-gold-600" />
                <div>
                  <div className="text-2xl font-bold text-black">{totalGDEntries}</div>
                  <p className="text-sm text-gray-600">Total GD Entries</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <div>
                  <div className="text-2xl font-bold text-green-600">{avgCompletionRate}%</div>
                  <p className="text-sm text-gray-600">Avg Completion Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Medal className="h-5 w-5 text-purple-600" />
                <div>
                  <div className="text-lg font-bold text-purple-600">{topPerformer?.officerName}</div>
                  <p className="text-sm text-gray-600">Top Performer</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border border-gray-200 hidden">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Police Station</Label>
                <Select
                  value={filters.policeStation}
                  onValueChange={(value) => setFilters({ ...filters, policeStation: value })}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select station" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {policeStations.map((station) => (
                      <SelectItem key={station} value={station}>
                        {station}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Department</Label>
                <Select
                  value={filters.department}
                  onValueChange={(value) => setFilters({ ...filters, department: value })}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Rank</Label>
                <Select value={filters.rank} onValueChange={(value) => setFilters({ ...filters, rank: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select rank" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {ranks.map((rank) => (
                      <SelectItem key={rank} value={rank}>
                        {rank}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Time Period</Label>
                <Select
                  value={filters.dateRange}
                  onValueChange={(value) => setFilters({ ...filters, dateRange: value })}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    <SelectItem value="thisMonth">This Month</SelectItem>
                    <SelectItem value="lastMonth">Last Month</SelectItem>
                    <SelectItem value="quarter">This Quarter</SelectItem>
                    <SelectItem value="year">This Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by officer name, badge number, station..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-300"
                />
              </div>
              <Button onClick={applyFilters} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="leaderboard" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 bg-gray-100">
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-white">
              Leaderboard
            </TabsTrigger>
            {/* <TabsTrigger value="analytics" className="data-[state=active]:bg-white">
              Analytics
            </TabsTrigger> */}
            <TabsTrigger value="detailed" className="data-[state=active]:bg-white">
              Detailed View
            </TabsTrigger>
          </TabsList>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <div className="space-y-6">
              {/* Top Performers - Three Sections */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* This Month Top Performers */}
                <Card className="border-0 shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-4">
                    <div className="flex items-center gap-2 text-white">
                      <TrendingUp className="h-5 w-5" />
                      <h3 className="font-semibold text-lg">This Month</h3>
                    </div>
                    <p className="text-emerald-100 text-xs mt-1">Current month top performers</p>
                  </div>
                  <CardContent className="p-0">
                    {[...filteredData]
                      .sort((a, b) => b.thisMonth - a.thisMonth)
                      .slice(0, 3)
                      .map((officer, index) => (
                        <div
                          key={officer.id}
                          className={`flex items-center gap-3 px-5 py-3.5 ${index !== 2 ? "border-b border-gray-100" : ""} hover:bg-gray-50 transition-colors`}
                        >
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                            index === 0 ? "bg-gradient-to-br from-yellow-400 to-amber-500 shadow-md shadow-amber-200" :
                            index === 1 ? "bg-gradient-to-br from-gray-300 to-gray-500 shadow-md shadow-gray-200" :
                            "bg-gradient-to-br from-orange-300 to-orange-500 shadow-md shadow-orange-200"
                          }`}>
                            {index === 0 ? <Trophy className="h-4 w-4" /> : index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm truncate">{officer.officerName}</div>
                            <div className="text-xs text-gray-500 truncate">{officer.policeStation}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-xl font-bold text-emerald-600">{officer.thisMonth}</div>
                            <div className="text-[10px] text-gray-400 uppercase tracking-wider">entries</div>
                          </div>
                        </div>
                      ))}
                    {filteredData.length === 0 && (
                      <div className="px-5 py-8 text-center text-gray-400 text-sm">No data available</div>
                    )}
                  </CardContent>
                </Card>

                {/* Last Month Top Performers */}
                <Card className="border-0 shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4">
                    <div className="flex items-center gap-2 text-white">
                      <Medal className="h-5 w-5" />
                      <h3 className="font-semibold text-lg">Last Month</h3>
                    </div>
                    <p className="text-blue-100 text-xs mt-1">Previous month top performers</p>
                  </div>
                  <CardContent className="p-0">
                    {[...filteredData]
                      .sort((a, b) => b.lastMonth - a.lastMonth)
                      .slice(0, 3)
                      .map((officer, index) => (
                        <div
                          key={officer.id}
                          className={`flex items-center gap-3 px-5 py-3.5 ${index !== 2 ? "border-b border-gray-100" : ""} hover:bg-gray-50 transition-colors`}
                        >
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                            index === 0 ? "bg-gradient-to-br from-yellow-400 to-amber-500 shadow-md shadow-amber-200" :
                            index === 1 ? "bg-gradient-to-br from-gray-300 to-gray-500 shadow-md shadow-gray-200" :
                            "bg-gradient-to-br from-orange-300 to-orange-500 shadow-md shadow-orange-200"
                          }`}>
                            {index === 0 ? <Trophy className="h-4 w-4" /> : index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm truncate">{officer.officerName}</div>
                            <div className="text-xs text-gray-500 truncate">{officer.policeStation}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-xl font-bold text-blue-600">{officer.lastMonth}</div>
                            <div className="text-[10px] text-gray-400 uppercase tracking-wider">entries</div>
                          </div>
                        </div>
                      ))}
                    {filteredData.length === 0 && (
                      <div className="px-5 py-8 text-center text-gray-400 text-sm">No data available</div>
                    )}
                  </CardContent>
                </Card>

                {/* Overall Top Performers */}
                <Card className="border-0 shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-[#0d2846] to-[#1a3d5c] px-5 py-4">
                    <div className="flex items-center gap-2 text-white">
                      <Trophy className="h-5 w-5" />
                      <h3 className="font-semibold text-lg">Overall</h3>
                    </div>
                    <p className="text-blue-200 text-xs mt-1">All-time top performers</p>
                  </div>
                  <CardContent className="p-0">
                    {[...filteredData]
                      .sort((a, b) => b.gdEntriesCount - a.gdEntriesCount)
                      .slice(0, 3)
                      .map((officer, index) => (
                        <div
                          key={officer.id}
                          className={`flex items-center gap-3 px-5 py-3.5 ${index !== 2 ? "border-b border-gray-100" : ""} hover:bg-gray-50 transition-colors`}
                        >
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                            index === 0 ? "bg-gradient-to-br from-yellow-400 to-amber-500 shadow-md shadow-amber-200" :
                            index === 1 ? "bg-gradient-to-br from-gray-300 to-gray-500 shadow-md shadow-gray-200" :
                            "bg-gradient-to-br from-orange-300 to-orange-500 shadow-md shadow-orange-200"
                          }`}>
                            {index === 0 ? <Trophy className="h-4 w-4" /> : index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm truncate">{officer.officerName}</div>
                            <div className="text-xs text-gray-500 truncate">{officer.policeStation}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-xl font-bold text-[#0d2846]">{officer.gdEntriesCount}</div>
                            <div className="text-[10px] text-gray-400 uppercase tracking-wider">entries</div>
                          </div>
                        </div>
                      ))}
                    {filteredData.length === 0 && (
                      <div className="px-5 py-8 text-center text-gray-400 text-sm">No data available</div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Performance Chart */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black">GD Entries by Officer</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="name" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Bar dataKey="gdEntries" fill="#000000" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black">Completion Rate Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "90-100%",
                            value: filteredData.filter((o) => o.completionRate >= 90).length,
                          },
                          {
                            name: "80-89%",
                            value: filteredData.filter((o) => o.completionRate >= 80 && o.completionRate < 90).length,
                          },
                          {
                            name: "70-79%",
                            value: filteredData.filter((o) => o.completionRate >= 70 && o.completionRate < 80).length,
                          },
                          {
                            name: "Below 70%",
                            value: filteredData.filter((o) => o.completionRate < 70).length,
                          },
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {[0, 1, 2, 3].map((entry, index) => (
                          <Cell key={`cell-${index}-${entry}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black">Department Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {departments.slice(1).map((dept) => {
                      const deptOfficers = filteredData.filter((o) => o.department === dept)
                      const totalEntries = deptOfficers.reduce((sum, o) => sum + o.gdEntriesCount, 0)
                      const avgCompletion = deptOfficers.length
                        ? Math.round(deptOfficers.reduce((sum, o) => sum + o.completionRate, 0) / deptOfficers.length)
                        : 0

                      return (
                        <div key={dept} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-black">{dept}</div>
                            <div className="text-sm text-gray-600">{deptOfficers.length} officers</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-black">{totalEntries}</div>
                            <div className="text-sm text-gray-600">{avgCompletion}% completion</div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Detailed View Tab */}
          <TabsContent value="detailed">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">
                  Detailed Officer Performance ({filteredData.length} officers)
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[42vh] overflow-y-auto">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">Rank</TableHead>
                        <TableHead className="text-black font-semibold">Officer Name</TableHead>
                        <TableHead className="text-black font-semibold">Badge No.</TableHead>
                        <TableHead className="text-black font-semibold">Station</TableHead>
                        {/* <TableHead className="text-black font-semibold">Department</TableHead> */}
                        <TableHead className="text-black font-semibold">Total GD</TableHead>
                        <TableHead className="text-black font-semibold">This Month</TableHead>
                        {/* <TableHead className="text-black font-semibold">Completion Rate</TableHead> */}
                        {/* <TableHead className="text-black font-semibold">Avg Time</TableHead> */}
                        {/* <TableHead className="text-black font-semibold">Trend</TableHead> */}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData
                        .sort((a, b) => b.gdEntriesCount - a.gdEntriesCount)
                        .map((officer, index) => (
                          <TableRow key={officer.id} className="hover:bg-gray-50">
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {index < 3 && (
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                      index === 0 ? "bg-yellow-500" : index === 1 ? "bg-gray-500" : "bg-orange-500"
                                    }`}
                                  >
                                    {index + 1}
                                  </div>
                                )}
                                {getRankBadge(officer.rank)}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium text-black">{officer.officerName}</TableCell>
                            <TableCell className="text-gray-600">{officer.badgeNumber}</TableCell>
                            <TableCell className="text-gray-600">{officer.policeStation}</TableCell>
                            {/* <TableCell className="text-gray-600">{officer.department}</TableCell> */}
                            <TableCell className="font-bold text-black">{officer.gdEntriesCount}</TableCell>
                            <TableCell className="text-gray-600">{officer.thisMonth}</TableCell>
                            {/* <TableCell>
                              <Badge
                                className={
                                  officer.completionRate >= 90
                                    ? "bg-green-100 text-green-800"
                                    : officer.completionRate >= 80
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-red-100 text-red-800"
                                }
                              >
                                {officer.completionRate}%
                              </Badge>
                            </TableCell> */}
                            {/* <TableCell className="text-gray-600">{officer.avgProcessingTime}</TableCell> */}
                            {/* <TableCell>{getTrendBadge(officer.trend, officer.trendPercentage)}</TableCell> */}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
